import { useState } from 'react';
import { Edit, Trash2, MessageCircle, Lock } from 'lucide-react';
import CommentReplyForm from './CommentReplyForm';

interface Comment {
  id: string;
  user_id: string;
  user_name: string;
  content: string;
  is_private: boolean;
  created_at: string;
  updated_at: string;
  replies?: Comment[];
}

interface CommentItemProps {
  comment: Comment;
  classId: string;
  currentUserId: string | null;
  isProvider: boolean;
  onEdit: (commentId: string, content: string) => void;
  onDelete: (commentId: string) => void;
  onReply: (parentId: string, content: string, isPrivate: boolean) => void;
  level?: number; // 댓글 depth (0 = 최상위, 1 = 답글)
}

export default function CommentItem({
  comment,
  classId,
  currentUserId,
  isProvider,
  onEdit,
  onDelete,
  onReply,
  level = 0,
}: CommentItemProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(comment.content);
  const [showReplyForm, setShowReplyForm] = useState(false);

  const isAuthor = currentUserId === comment.user_id;
  const canSee = !comment.is_private || isAuthor || isProvider;

  if (!canSee) {
    return null; // 비공개 댓글이고 권한이 없으면 숨김
  }

  const handleSaveEdit = () => {
    if (editContent.trim()) {
      onEdit(comment.id, editContent.trim());
      setIsEditing(false);
    }
  };

  const handleCancelEdit = () => {
    setEditContent(comment.content);
    setIsEditing(false);
  };

  const handleReplySubmit = (content: string, isPrivate: boolean) => {
    onReply(comment.id, content, isPrivate);
    setShowReplyForm(false);
  };

  return (
    <div className={`${level > 0 ? 'ml-12 mt-3' : 'mt-4'}`}>
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        {/* 헤더 */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center text-sm font-bold">
              {comment?.user_name?.[0]?.toUpperCase() || '?'}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-gray-900">{comment?.user_name || '알 수 없음'}</span>
                {comment.is_private && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                    <Lock className="w-3 h-3" />
                    비공개
                  </span>
                )}
              </div>
              <span className="text-xs text-gray-500">
                {new Date(comment.created_at).toLocaleString('ko-KR')}
              </span>
            </div>
          </div>

          {/* 액션 버튼 (본인 댓글만) */}
          {isAuthor && !isEditing && (
            <div className="flex items-center gap-1">
              <button
                onClick={() => setIsEditing(true)}
                className="p-1 text-gray-400 hover:text-primary transition-colors"
                title="수정"
              >
                <Edit className="w-4 h-4" />
              </button>
              <button
                onClick={() => {
                  if (confirm('댓글을 삭제하시겠습니까?')) {
                    onDelete(comment.id);
                  }
                }}
                className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                title="삭제"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        {/* 내용 */}
        {isEditing ? (
          <div className="space-y-2">
            <textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary resize-none text-sm"
            />
            <div className="flex gap-2">
              <button
                onClick={handleSaveEdit}
                className="px-3 py-1 bg-primary text-white rounded text-sm hover:bg-primary-600 transition-colors"
              >
                저장
              </button>
              <button
                onClick={handleCancelEdit}
                className="px-3 py-1 bg-gray-100 text-gray-700 rounded text-sm hover:bg-gray-200 transition-colors"
              >
                취소
              </button>
            </div>
          </div>
        ) : (
          <>
            <p className="text-gray-700 text-sm whitespace-pre-wrap">{comment.content}</p>

            {/* 답글 버튼 (최상위 댓글만) */}
            {level === 0 && (
              <button
                onClick={() => setShowReplyForm(!showReplyForm)}
                className="mt-3 text-xs text-gray-500 hover:text-primary flex items-center gap-1 transition-colors"
              >
                <MessageCircle className="w-3 h-3" />
                답글 {comment.replies && comment.replies.length > 0 ? `(${comment.replies.length})` : ''}
              </button>
            )}
          </>
        )}
      </div>

      {/* 답글 폼 */}
      {showReplyForm && level === 0 && (
        <div className="ml-12 mt-3">
          <CommentReplyForm
            onSubmit={handleReplySubmit}
            onCancel={() => setShowReplyForm(false)}
            placeholder={`${comment.user_name}님에게 답글 작성...`}
          />
        </div>
      )}

      {/* 답글 목록 */}
      {comment.replies && comment.replies.length > 0 && level === 0 && (
        <div className="mt-3">
          {comment.replies.map((reply) => (
            <CommentItem
              key={reply.id}
              comment={reply}
              classId={classId}
              currentUserId={currentUserId}
              isProvider={isProvider}
              onEdit={onEdit}
              onDelete={onDelete}
              onReply={onReply}
              level={1}
            />
          ))}
        </div>
      )}
    </div>
  );
}
